import { formatDateRu } from "../../utils/dateFormat";
import { formatCompensation } from "../../utils/compensation";
import { getCandidateNumerology } from "../../utils/candidateNumerology";
import { phoneDigits, usageShort } from "../../utils/phoneFormat";
import ContactValues, { contactItems } from "./ContactValues";

/**
 * Columns of the employees table (Чат 4, правка №15). [Claude] 2026-11-02
 * Every user picks which of them are shown and in which order (ui-preferences "employees_table").
 * «Оплата» is offered only to users with the «ЗП сотрудников» right (ctx.canSeeSalary); rows the user
 * may not see come with salary_hidden=true and show «скрыто».
 * Photos of employees are a gallery in the card (for congratulations), not an avatar — no photo column here.
 */

export const DEFAULT_EMPLOYEE_COLUMNS = [
    "full_name", "department", "position", "phone_number", "date_hired", "tenure", "adaptation",
];

const DAY_MS = 24 * 60 * 60 * 1000;

const toDate = (value) => {
    if (!value) return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

/** Full months between two dates. */
export function monthsBetween(from, to) {
    if (!from || !to || to < from) return null;
    let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (to.getDate() < from.getDate()) months -= 1;
    return Math.max(0, months);
}

/** «2 г. 3 мес.», «5 мес.», «12 дн.» — tenure in the company (up to dismissal for former employees). */
export function formatTenure(employee, today = new Date()) {
    const start = toDate(employee?.date_hired);
    if (!start) return null;
    const end = toDate(employee?.date_fired) || today;
    const months = monthsBetween(start, end);
    if (months === null) return null;
    if (months === 0) {
        const days = Math.max(0, Math.floor((end - start) / DAY_MS));
        return `${days} дн.`;
    }
    const years = Math.floor(months / 12);
    const rest = months % 12;
    return [years ? `${years} г.` : null, rest ? `${rest} мес.` : null].filter(Boolean).join(" ");
}

export function tenureDays(employee, today = new Date()) {
    const start = toDate(employee?.date_hired);
    if (!start) return null;
    const end = toDate(employee?.date_fired) || today;
    return Math.floor((end - start) / DAY_MS);
}

export function ageOf(employee, today = new Date()) {
    const birth = toDate(employee?.birth_date);
    if (!birth) return employee?.age ?? null;
    const months = monthsBetween(birth, today);
    return months === null ? null : Math.floor(months / 12);
}

const GENDER = { male: "Мужской", female: "Женский" };

/** «02.01.1990, 2/4» — дата рождения с числами судьбы (ЧС/ЧМ), как в карточке кандидата. */
export function birthWithNumerology(value) {
    if (!value) return null;
    const { cs, cm } = getCandidateNumerology(value);
    return `${formatDateRu(value)}, ${cs ?? "—"}/${cm ?? "—"}`;
}

export const ADAPTATION_STAGE = {
    week_1: "Неделя 1",
    month_1: "Месяц 1",
    month_2: "Месяц 2",
    control_2m: "Контроль 2 мес.",
    extra: "Доп. срез",
};

const RISK = { low: "низкий", medium: "средний", high: "высокий" };

/** Short text for the «Адаптация» cell; `adaptation` is a row of /v1/employees-table/adaptation. */
export function adaptationText(adaptation) {
    if (!adaptation) return "—";
    if (adaptation.state === "finished") return "Завершена";
    const progress = adaptation.total ? `${adaptation.done}/${adaptation.total}` : "";
    const stage = adaptation.next_kind ? (ADAPTATION_STAGE[adaptation.next_kind] || adaptation.next_kind) : "";
    return [stage, progress].filter(Boolean).join(" · ") || "Идёт";
}

function AdaptationCell({ adaptation }) {
    if (!adaptation) return <span className="text-slate-400">—</span>;
    const risk = adaptation.risk ? (RISK[adaptation.risk] || adaptation.risk) : null;
    return <span className="inline-flex items-center gap-1.5" title={adaptation.next_date ? `Следующий срез: ${formatDateRu(adaptation.next_date)}` : undefined}>
        <span className={adaptation.overdue ? "font-semibold text-red-600" : adaptation.state === "finished" ? "text-emerald-700" : ""}>{adaptationText(adaptation)}</span>
        {adaptation.overdue ? <span className="rounded bg-red-50 px-1 text-[11px] font-semibold text-red-600">просрочен</span> : null}
        {risk && adaptation.state !== "finished" ? <span className="rounded bg-slate-100 px-1 text-[11px] text-slate-500">риск: {risk}</span> : null}
    </span>;
}

/**
 * Builds the column catalog. ctx: { adaptationByEmployee: Map(id → summary), onOpen(employee) }.
 */
export function buildEmployeeColumns({ adaptationByEmployee = new Map(), contactsByEmployee = new Map(), onOpen, canSeeSalary = false } = {}) {
    const adaptation = (row) => adaptationByEmployee.get(row.id);
    // Телефон / Email / Telegram: значение из профиля и все из «Контактов», у каждого — чей он (доработка №15, 04.11).
    const contactColumn = (id, label, width, type) => ({
        id, label, width,
        value: (row) => contactItems(row, contactsByEmployee.get(row.id), type).map((x) => x.value).join(" ") || null,
        render: (row) => <ContactValues type={type} items={contactItems(row, contactsByEmployee.get(row.id), type)} />,
    });
    return [
        {
            id: "full_name", label: "ФИО", width: 240, pinned: true, noRowClick: true,
            value: (row) => row.full_name,
            render: (row) => <span className="flex flex-col items-start">
                <button type="button" onClick={() => onOpen?.(row)} className="break-words text-left font-semibold text-slate-900 hover:text-amber-700">{row.full_name}</button>
                {row._match && (row._match.field || row.date_fired) ? <span className="text-[11px] font-normal text-slate-500" data-testid={`employee-match-${row.id}`}>
                    {row.date_fired ? <span className="mr-1 rounded bg-red-50 px-1 font-semibold text-red-600">уволен {formatDateRu(row.date_fired)}</span> : null}
                    {row._match.field ? `найдено: ${row._match.field} ${row._match.value}` : null}
                </span> : null}
            </span>,
        },
        { id: "department", label: "Отдел", width: 150 },
        { id: "position", label: "Должность", width: 190 },
        contactColumn("phone_number", "Телефон", 175, "phone"),
        contactColumn("email", "Email", 210, "email"),
        contactColumn("telegram_username", "Telegram", 160, "telegram"),
        { id: "date_hired", label: "Дата выхода", width: 120, value: (row) => toDate(row.date_hired), render: (row) => formatDateRu(row.date_hired) },
        { id: "tenure", label: "Стаж", width: 110, value: (row) => tenureDays(row), render: (row) => dash(formatTenure(row)) },
        {
            id: "adaptation", label: "Адаптация", width: 210,
            value: (row) => { const a = adaptation(row); return a ? `${a.overdue ? 0 : a.state === "finished" ? 2 : 1}${a.next_date || ""}` : null; },
            render: (row) => <AdaptationCell adaptation={adaptation(row)} />,
        },
        { id: "birth_date", label: "Дата рождения", width: 150, value: (row) => toDate(row.birth_date), render: (row) => dash(birthWithNumerology(row.birth_date)) },
        { id: "age", label: "Возраст", width: 90, value: (row) => ageOf(row), render: (row) => dash(ageOf(row)) },
        { id: "gender", label: "Пол", width: 100, render: (row) => GENDER[row.gender] || dash(row.gender) },
        { id: "marital_status", label: "Семейное положение", width: 160, render: (row) => dash(row.marital_status) },
        { id: "legal_entity", label: "Юрлицо", width: 140, render: (row) => dash(row.legal_entity) },
        { id: "work_schedule", label: "График", width: 110, render: (row) => dash(row.work_schedule) },
        ...(canSeeSalary ? [{
            id: "salary", label: "Оплата", width: 170,
            value: (row) => (row.salary_hidden ? null : (row.salary != null ? Number(row.salary) : row.salary_terms || null)),
            render: (row) => (row.salary_hidden ? <span className="text-slate-400">скрыто</span> : <span title={row.salary_terms || ""}>{formatCompensation(row)}</span>),
        }] : []),
        { id: "hobbies", label: "Хобби", width: 200, value: (row) => (row.hobbies || []).join(", "), render: (row) => dash((row.hobbies || []).join(", ")) },
        { id: "erp_account", label: "Учётка ERP", width: 110, value: (row) => (row.erp_user_id ? 1 : 0), render: (row) => (row.erp_user_id ? "есть" : "нет") },
        { id: "date_fired", label: "Дата увольнения", width: 140, value: (row) => toDate(row.date_fired), render: (row) => formatDateRu(row.date_fired) },
        { id: "termination_reason", label: "Причина увольнения", width: 220, render: (row) => <span title={row.termination_reason || ""}>{dash(row.termination_reason)}</span> },
    ];
}

/** Text used by the page search: every field a person may type (+ «Контакты»). */
export function employeeSearchText(row, contacts = []) {
    const phones = contactItems(row, contacts, "phone").map((x) => x.value);
    return [row.full_name, row.department, row.position,
        ...contactItems(row, contacts, "email").map((x) => x.value),
        ...contactItems(row, contacts, "telegram").map((x) => x.value),
        ...phones, ...phones.map((p) => phoneDigits(p) || String(p).replace(/\D/g, ""))]
        .filter(Boolean).join(" ").toLocaleLowerCase("ru");
}

const TYPE_WORD = { phone: "тел.", email: "почта", telegram: "Telegram" };

/**
 * Why a row matches the page search (доработка №15, 04.11): null — no match; { field: null } — by name,
 * department or position; { field: "тел. (раб.)", value } — by a phone / e-mail / Telegram of the profile
 * or of «Контакты». Phones match with or without +7 / 8, spaces and dashes.
 */
export function employeeMatch(row, contacts = [], query = "") {
    const q = String(query || "").trim().toLocaleLowerCase("ru");
    if (!q) return { field: null };
    const plain = [row.full_name, row.department, row.position].filter(Boolean).join(" ").toLocaleLowerCase("ru");
    if (plain.includes(q)) return { field: null };
    let digits = q.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("8")) digits = `7${digits.slice(1)}`;
    const shortDigits = digits.length >= 4 && digits.startsWith("8") ? digits.slice(1) : null;
    const text = q.replace(/^@/, "");
    for (const type of ["phone", "email", "telegram"]) {
        for (const item of contactItems(row, contacts, type)) {
            const value = String(item.value || "").toLocaleLowerCase("ru");
            const valueDigits = type === "phone" ? (phoneDigits(item.value) || value.replace(/\D/g, "")) : "";
            const hit = (text && value.replace(/^@/, "").includes(text))
                || (type === "phone" && digits.length >= 3 && (valueDigits.includes(digits) || (shortDigits && valueDigits.includes(shortDigits))));
            if (hit) return { field: `${TYPE_WORD[type]} (${usageShort(item.usage)})`, value: item.value };
        }
    }
    return null;
}
